import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { AlertCircle, ArrowRightLeft, Loader2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Textarea,
  cn,
  toast,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  type Student,
  type StudentStatus,
  type StudentTransitionInput,
  type TenantVerticalLiteral,
} from "@eduviaos/shared";
import { STUDENT_TRANSITION_STATUSES, useTransitionStudent } from "@/hooks/useStudents";
import { useTerms } from "@/hooks/useTerms";

/**
 * Phase J1.b — Student status-transition dialog.
 *
 * Modal because the audit context is critical — the admin needs to
 * commit to a reason before the BE writes the `student.transitioned`
 * audit row. The drawer would let them lose focus and abandon
 * mid-thought; a modal forces a clean decision.
 *
 * Terminal states (`graduated` / `withdrawn` / `transferred`) accept an
 * optional `terminating_date`; if omitted the BE stamps today (IST).
 */

function statusToPill(status: StudentStatus): StatusPillStatus {
  switch (status) {
    case "active":
      return "active";
    case "paused":
      return "invited";
    case "graduated":
    case "withdrawn":
    case "transferred":
      return "archived";
    default:
      return "inactive";
  }
}

function statusLabel(status: StudentStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export interface StudentTransitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student: Student | null;
  onTransitioned?: (saved: Student) => void;
  vertical?: TenantVerticalLiteral;
}

export function StudentTransitionDialog({
  open,
  onOpenChange,
  student,
  onTransitioned,
  vertical,
}: StudentTransitionDialogProps) {
  const { ct, terms } = useTerms(vertical);
  const personOne = terms.learner.one.toLowerCase();
  const reduceMotion = useReducedMotion();
  const mutation = useTransitionStudent();

  const currentStatus = student?.status as StudentStatus | undefined;

  const eligible = React.useMemo(
    () => STUDENT_TRANSITION_STATUSES.filter((s) => s.value !== currentStatus),
    [currentStatus],
  );

  const [newStatus, setNewStatus] = React.useState<StudentStatus | "">("");
  const [reason, setReason] = React.useState("");
  const [terminatingDate, setTerminatingDate] = React.useState("");
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setNewStatus(eligible[0]?.value ?? "");
      setReason("");
      setTerminatingDate("");
      setSubmitError(null);
    }
  }, [open, eligible]);

  const selected = STUDENT_TRANSITION_STATUSES.find((s) => s.value === newStatus);
  const isTerminal = selected?.terminal === true;
  const trimmedReason = reason.trim();
  const canSubmit = !!student && !!newStatus && trimmedReason.length > 0 && !mutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!canSubmit || !student) return;

    const body: StudentTransitionInput = {
      new_status: newStatus as StudentStatus,
      reason: trimmedReason,
    };
    const date = terminatingDate.trim();
    if (date && isTerminal) body.terminating_date = date;

    try {
      const saved = await mutation.mutateAsync({ id: student.id, body });
      onTransitioned?.(saved);
      // Phase E1.b.2 — success toast on the global `<Toaster>`. The
      // in-dialog `role="alert"` banner stays for server-side
      // validation errors (e.g. invalid date for non-terminal status)
      // because those are recoverable in editing context.
      toast.success(`${saved.name} moved to ${statusLabel(saved.status as StudentStatus)}.`);
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || `Couldn't change this ${personOne}'s status.`
          : err instanceof Error
            ? err.message
            : `Couldn't change this ${personOne}'s status.`;
      setSubmitError(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="student-transition-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <ArrowRightLeft className="size-5" />
            </span>
            <div className="flex-1">
              <DialogTitle className="tracking-tight">Change status</DialogTitle>
              <DialogDescription className="mt-1 text-xs">
                Audited transition for {student?.name ?? `this ${personOne}`}. The reason is
                recorded permanently.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="student-transition-form">
          {currentStatus ? (
            <div className="bg-muted/30 flex items-center gap-2 rounded-md px-3 py-2 text-xs">
              <span className="text-muted-foreground">Current:</span>
              <StatusPill status={statusToPill(currentStatus)} label={statusLabel(currentStatus)} />
            </div>
          ) : null}

          <AnimatePresence>
            {submitError ? (
              <motion.div
                initial={reduceMotion ? false : { opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
                transition={{ duration: 0.16, ease: "easeOut" }}
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span>{submitError}</span>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <div>
            <Label htmlFor="student-transition-status" className="text-xs">
              New status
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Select
              value={newStatus === "" ? undefined : newStatus}
              onValueChange={(v) => setNewStatus(v as StudentStatus)}
            >
              <SelectTrigger id="student-transition-status" className="mt-1">
                <SelectValue placeholder="Pick a target status" />
              </SelectTrigger>
              <SelectContent>
                {eligible.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="flex flex-col">
                      <span className="font-medium">{opt.label}</span>
                      <span className="text-muted-foreground text-xs">{ct(opt.description)}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="student-transition-reason" className="text-xs">
              Reason
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Textarea
              id="student-transition-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder={
                terms.guardian.one === "Parent"
                  ? "e.g. Moved to a different city for parent's job transfer."
                  : "e.g. Moved to a different city for a new job."
              }
              required
              className="mt-1"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              {trimmedReason.length}/2000 — written to the audit trail.
            </p>
          </div>

          {isTerminal ? (
            <div>
              <Label htmlFor="student-transition-date" className="text-xs">
                Effective date
              </Label>
              <Input
                id="student-transition-date"
                type="date"
                value={terminatingDate}
                onChange={(e) => setTerminatingDate(e.target.value)}
                className="mt-1"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                If left blank, today&apos;s date is used.
              </p>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="ghost"
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              Apply transition
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
